/**
 * Snapshot Viewer — Renders a saved snapshot in the current page so the
 * Inspector + Sidebar can be pointed at it for translation.
 *
 * When the editor loads with `?__lr_snapshot={uuid}`, the normal page is
 * replaced with the snapshot HTML inside a sandbox wrapper:
 *
 *   <div id="__lr_snapshot_root">  ← scoped root passed to Inspector
 *     <html>...snapshot...</html>
 *   </div>
 *
 * Auto-init retranslation is NOT re-run against the snapshot — translations
 * saved via the sidebar flow through the normal batch endpoint and will
 * appear on the real auth page on next load.
 */

import type { EditorAPI } from './editor-api.js';
import type { SnapshotTextEntry } from './snapshot-capture.js';
import { sanitizeElement, sanitizeStyleSheetText } from '../sanitizer.js';

export interface SnapshotViewerResult {
  /** Scope root to pass to Inspector */
  root: HTMLElement;
  /** Snapshot metadata for display in the toolbar / sidebar */
  pageUrl: string;
  title: string;
  /** Pre-computed key map: blockIndex → key. The Inspector uses this to resolve
   *  clicks without re-running BlockExtractor on the redacted HTML. */
  keyMap: Map<number, string>;
}

/**
 * Fetch a snapshot from the server and mount it into the current page.
 * Returns the root element Inspector should scope against.
 *
 * On failure (404, network error, deleted snapshot) returns null and displays
 * a user-visible notice via the caller's notice helper.
 */
export async function activateSnapshotViewer(
  snapshotUuid: string,
  api: EditorAPI,
): Promise<SnapshotViewerResult | null> {
  const snapshot = await api.getSnapshot(snapshotUuid);
  if (!snapshot) return null;

  // Replace the entire body with a sandbox wrapper holding the snapshot HTML.
  // We keep document.head alone so the editor's Shadow DOM host and existing
  // stylesheets (for rendering) still work.
  //
  // The snapshot HTML is a full document (starts with <!DOCTYPE>), so we
  // parse it with DOMParser and extract the <body> children. The snapshot's
  // <head> <link> and <style> tags are appended to the real head so the
  // snapshot renders with its original styles.
  const parser = new DOMParser();
  const doc = parser.parseFromString(snapshot.html, 'text/html');

  // Snapshot HTML is server-fetched and must be treated as tamperable —
  // it mounts in the editor realm, which holds the PM's session JWT.
  // Strip executable elements / handlers / dangerous URLs before any of it
  // touches the live document (core Phase 4).
  sanitizeElement(doc.body);

  // Copy over stylesheets and inline <style> tags from the snapshot head.
  // We prefix them with a data attribute so the cleanup code can remove them
  // if the viewer is ever unmounted (out of scope for Phase 1).
  // Cross-origin <link> stylesheets are dropped (a tampered snapshot could
  // pull attacker CSS); inline <style> text is scrubbed of @import and
  // cross-origin url() beacons.
  const snapshotStyles = doc.head.querySelectorAll('link[rel="stylesheet"], style');
  for (let i = 0; i < snapshotStyles.length; i++) {
    const source = snapshotStyles[i];
    if (source.tagName.toLowerCase() === 'link') {
      const href = source.getAttribute('href') ?? '';
      let sameOrigin = false;
      try {
        sameOrigin =
          new URL(href, window.location.href).origin === window.location.origin;
      } catch {
        /* unparseable href → drop */
      }
      if (!sameOrigin) continue;
      const clone = source.cloneNode(true) as HTMLElement;
      clone.setAttribute('data-lr-snapshot-style', '');
      document.head.appendChild(clone);
    } else {
      const style = document.createElement('style');
      style.textContent = sanitizeStyleSheetText(
        source.textContent ?? '',
        window.location.href,
      );
      style.setAttribute('data-lr-snapshot-style', '');
      document.head.appendChild(style);
    }
  }

  // Copy <title> so the tab title reflects the captured page
  if (snapshot.title) {
    document.title = `[Snapshot] ${snapshot.title}`;
  }

  // Clear the current body content (but preserve the editor's shadow host
  // if it's already mounted — it's attached to #__lionrapid-editor).
  const editorHost = document.getElementById('__lionrapid-editor');
  while (document.body.firstChild) {
    if (document.body.firstChild === editorHost) {
      // Skip past the editor host without removing it
      const next = document.body.firstChild.nextSibling;
      if (!next) break;
      document.body.removeChild(next);
    } else {
      document.body.removeChild(document.body.firstChild);
    }
  }

  // Build sandbox wrapper and inject snapshot body
  const root = document.createElement('div');
  root.id = '__lr_snapshot_root';
  root.setAttribute('data-lr-snapshot-uuid', snapshotUuid);
  root.innerHTML = doc.body.innerHTML;

  // Insert wrapper before the editor host so the editor stays on top
  if (editorHost && editorHost.parentNode === document.body) {
    document.body.insertBefore(root, editorHost);
  } else {
    document.body.appendChild(root);
  }

  // Build key map from textEntries: blockIndex → auto_key
  const keyMap = new Map<number, string>();
  const entries: SnapshotTextEntry[] = snapshot.textEntries || [];
  for (const entry of entries) {
    keyMap.set(entry.blockIndex, entry.key);
  }

  return {
    root,
    pageUrl: snapshot.pageUrl,
    title: snapshot.title,
    keyMap,
  };
}
